import { WalletPayment } from '@aaa-ncnu-ie/mypolicy-api-billing';
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import { useWalletQuery } from '../../api/wallet/getWallet';
import colors from '../../constants/colors';
import paymentTypeMap, {
  getPaymentCommonName,
} from '../../constants/paymentTypeNames';
import useAnalytics from '../../hooks/useAnalytics';
import useGetCustKey from '../../hooks/useGetCustKey';
import { usePaymentsContext } from '../../store/payments';
import { setSelectedPaymentMethod } from '../../store/paymentsReducer';
import { EditButton } from '../styledComponents/IconButtons';
import CircularProgress from '../CircularProgress/CircularProgress';

interface WalletSelectionProps {
  setSelectedPaymentType: (method: 'EFT' | 'CRDC') => void;
  selectedPaymentAccountToken: string;
  setSelectedPaymentAccountToken: (token: string) => void;
}

const WalletSelection = ({
  setSelectedPaymentType,
  setSelectedPaymentAccountToken,
  selectedPaymentAccountToken,
}: WalletSelectionProps) => {
  const { dispatch, state } = usePaymentsContext();
  const location = useLocation();
  const custKey = useGetCustKey();
  const { data: wallet, isLoading, error } = useWalletQuery(custKey);
  const { track } = useAnalytics();
  const [focusedPaymentOption, setFocusedPaymentOption] = useState<
    string | null
  >(null);

  const walletAccounts: WalletPayment[] = wallet?.accounts ?? [];

  const walletEmpty = !isLoading && (error || walletAccounts.length === 0);

  useEffect(() => {
    if (selectedPaymentAccountToken && wallet?.accounts) {
      const payment = wallet?.accounts?.find(
        (account) => account.paymentAccountToken === selectedPaymentAccountToken
      );
      dispatch(
        setSelectedPaymentMethod({
          analyticsAccountType: getPaymentCommonName(
            payment.card?.type ?? payment.account?.type
          ),
          name:
            payment?.name ??
            payment?.card?.holderName ??
            payment?.account?.holderName,
          paymentToken: payment.paymentToken ?? payment.paymentAccountToken,
          paymentMethod:
            payment.paymentMethod ??
            (payment.card && 'CRDC') ??
            (payment.account && 'EFT'),
          expirationDate:
            payment.expirationDate ?? payment.card?.expirationDate ?? '',
          zipCode: payment.zipCode ?? payment.card?.zipCode ?? '',
          displayPaymentMethod:
            payment.displayPaymentMethod ??
            `${
              paymentTypeMap[payment.card?.type ?? payment.account?.type]
            } ****${payment.card?.number ?? payment.account?.number}`,
        })
      );
    }
  }, [selectedPaymentAccountToken]);

  const getAccountLabel = (account: WalletPayment): string => {
    if (account.shortName) {
      return account.shortName;
    }

    if (account.card) {
      return `${paymentTypeMap[account.card.type]} ****${account.card.number}`;
    } else if (account.account) {
      return `${paymentTypeMap[account.account.type]} ****${
        account.account.number
      }`;
    } else {
      return 'Payment Method';
    }
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ width: 1 }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  return (
    <FormControl sx={{ mt: 2 }}>
      <Stack spacing={2}>
        {walletEmpty && <Navigate to="add" replace state={location.state} />}
        {walletAccounts.map((account: WalletPayment, idx) => {
          const cardExpired = account?.card ? account.card.expired : false;
          return (
            <FormControlLabel
              sx={{
                border:
                  selectedPaymentAccountToken === account.paymentAccountToken
                    ? '2px solid black'
                    : `1px solid ${colors.csaaNeutralBorder}`,
                ...(focusedPaymentOption === account.paymentAccountToken && {
                  borderColor: colors.csaaLight,
                }),
                borderRadius: 1,
                mx: 0,
                fontFamily: 'Cabin',
                fontSize: 18,
                fontWeight:
                  selectedPaymentAccountToken === account.paymentAccountToken
                    ? 700
                    : 400,
                cursor: cardExpired ? 'default' : 'initial',
                outline:
                  focusedPaymentOption === account.paymentAccountToken
                    ? `2px solid ${colors.csaaBlack}`
                    : 'none',
                outlineOffset: 2,
                '&:hover': {
                  background: colors.csaaOverlayBlack,
                  borderColor: colors.csaaMid,
                  color: colors.csaaMid,
                },
              }}
              key={`${account.shortName}-${idx}`}
              value={account.paymentAccountToken}
              control={
                <Radio
                  onFocus={() =>
                    setFocusedPaymentOption(account.paymentAccountToken)
                  }
                  onBlur={() => setFocusedPaymentOption(null)}
                  disabled={cardExpired}
                  sx={{
                    color: colors.csaaMid,
                    '&.Mui-checked': {
                      color: colors.csaaMid,
                    },
                  }}
                  checked={
                    selectedPaymentAccountToken === account.paymentAccountToken
                  }
                  disableRipple
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (account.card) {
                      setSelectedPaymentType('CRDC');
                    } else {
                      setSelectedPaymentType('EFT');
                    }

                    setSelectedPaymentAccountToken(event.target.value);
                  }}
                  inputProps={{
                    'aria-label': getAccountLabel(account),
                  }}
                />
              }
              label={
                <Stack
                  justifyContent="space-between"
                  direction="row"
                  sx={{ width: 1 }}
                >
                  <Stack
                    className="csaa-qm-block"
                    direction="row"
                    alignItems={{ mobile: 'flex-start', tablet: 'center' }}
                    sx={{
                      gap: { mobile: 1, tablet: 2 },
                      cursor: cardExpired ? 'default' : 'pointer',
                    }}
                  >
                    {getAccountLabel(account)}
                    {cardExpired && (
                      <Box
                        sx={{
                          backgroundColor: colors.csaaRed10,
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          height: 24,
                          alignSelf: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Cabin',
                            fontKerning: 'normal',
                            fontSize: 14,
                            lineHeight: '16px',
                            letterSpacing: 0.2,
                            color: colors.csaaDark,
                            fontWeight: 500,
                          }}
                        >
                          Expired
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  <EditButton
                    aria-label={`Edit payment method ${getAccountLabel(
                      account
                    )}`}
                    component={Link}
                    to="edit"
                    state={{ walletAccount: account, edit: true }}
                    sx={{
                      mr: 2,
                    }}
                    onClick={() => {
                      track('Payment Methods Accessed', {
                        event_type: 'Link Accessed',
                        location: 'Feature Card',
                        link: 'Pencil icon',
                        policy_number: state.singleSelectedPayment.policyNumber,
                        state: state.singleSelectedPayment.riskState,
                      });
                    }}
                  />
                </Stack>
              }
              disableTypography
            />
          );
        })}
      </Stack>
    </FormControl>
  );
};

export default WalletSelection;
